// @ts-nocheck
import { compact, debounce } from 'lodash';
import moment, { type Moment } from 'moment-timezone';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  ButtonUI,
  DateRangePicker,
  InputSearch,
  showNotification,
  TableListV2 as TableListTS,
  useQueryFetch
} from 'sonata-react-components';

import { getStaticMode } from '@/store/selectors/reporting.selectors';
import { downloadFileFromUrl } from '@/ts-helpers/fileDownload.helper';
import { deleteReportingDownload } from '../../../reporting.resources';
import { DeleteReportPrompt } from '../DeleteReportPrompt';
import { ReportsDownloadsTabStyle } from './ReportsDownloadsTab.style';

import type { ReportDownloadInterface } from 'sonata-static-storage/lib/schemas/report/reportDownload.schema';

type Props = {
  campaignsIds: string[];
  id: string;
  isDownloadLoading?: boolean;
  onDownloadMultipleReports: (ids: string[]) => void;
  onReportLoad: (report: ReportDownloadInterface) => void;
  userId: string;
};

type RangeDates = {
  endDate: Moment | null;
  startDate: Moment | null;
};

type State = {
  cacheSeed: (string | null)[];
  filterDateValues: RangeDates;
  idToDelete?: string;
  selectedReports: ReportDownloadInterface[];
  textSearch?: string;
};

const LAST_DOWNLOAD_FORMAT = 'DD MMM, YYYY; hh:mm A';

function ReportsDownloadsTab({
  campaignsIds,
  id,
  isDownloadLoading = false,
  onDownloadMultipleReports,
  onReportLoad,
  userId
}: Props) {
  const useBackups = useSelector(getStaticMode);
  const dateFormat = taptap.app.i18n_dates.getDateLocalizations().shortDate;
  const [{ cacheSeed, filterDateValues, idToDelete, selectedReports, textSearch }, setState] = useState<State>({
    cacheSeed: [new Date().toISOString(), null, null],
    filterDateValues: {
      endDate: null,
      startDate: null
    },
    selectedReports: []
  });
  const { formatMessage } = useIntl();

  const { isFetching: isDeleteFetching, refetch: refetchDelete } = useQueryFetch(
    ['downloadReportDelete', idToDelete],
    async () => deleteReportingDownload(idToDelete!),
    {
      enabled: false,
      onError: () => {
        setState(prevState => ({
          ...prevState,
          idToDelete: undefined
        }));
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Deletion failed',
            id: 'reporting.reportingDownload.myReportsModal.deletionFailed'
          })
        );
      },
      onSuccess: () => {
        setState(prevState => ({
          ...prevState,
          cacheSeed: [new Date().toISOString(), ...prevState.cacheSeed.slice(1)],
          idToDelete: undefined
        }));
      }
    }
  );

  const toolbar = {
    gridConfig: [
      {
        className: 'reportingDownloadsTitle',
        description: formatMessage({
          defaultMessage: 'Name',
          id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.table.header.name'
        }),
        key: 'name'
      },
      {
        className: 'reportingDownloadsSubTitle',
        description: formatMessage({
          defaultMessage: 'Last Download',
          id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.table.header.lastDownload'
        }),
        key: 'lastDownload'
      },
      {
        className: 'reportingDownloadsFileType',
        description: formatMessage({
          defaultMessage: 'Type',
          id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.table.header.fileType'
        }),
        key: 'fileType'
      }
    ],
    gridLayout: 'reportingDownloadedReportsToolbarLayout',
    showSelectAll: true
  };

  const defaultEmptyState = {
    description: formatMessage({
      defaultMessage: "Explore our content and select what you'd like to download. Start now and find what you need",
      id: 'reporting.reportingDownload.myReportsModal.listEmptyStyle.description'
    }),
    fontSizes: {
      description: '12px',
      icon: '18px',
      title: '14px'
    },
    icon: 'fal fa-bolt',
    show: true,
    spacing: 'small',
    title: formatMessage({
      defaultMessage: 'You don’t have any downloads yet',
      id: 'reporting.reportingDownload.myReportsModal.listEmptyStyle.title'
    })
  };

  const searchDefaultEmptyState = {
    fontSizes: {
      description: '12px',
      icon: '18px',
      title: '14px'
    },
    icon: 'fal fa-empty-set',
    show: true,
    spacing: 'small',
    title: formatMessage({
      defaultMessage: 'There is no data for the current search.',
      id: 'reporting.reportingDownload.myReportsModal.searchEmptyStyle.title'
    })
  };

  const handleSelectionChange = (rows: { entity: ReportDownloadInterface }[]) => {
    setState(prevState => ({
      ...prevState,
      selectedReports: rows.map(r => r.entity)
    }));
  };

  const handleSearchInputChange = debounce(newText => {
    setState(prevState => ({
      ...prevState,
      textSearch: newText
    }));
  }, 500);

  const handleDownloadMultipleReports = () => {
    onDownloadMultipleReports(compact(selectedReports.map(report => report._id)));
  };

  const handleNewFromReportClick = (report: ReportDownloadInterface) => {
    onReportLoad(report);
  };

  const handleDeleteScheduledReportClick = (scheduleId: string) => {
    setState(prevState => ({
      ...prevState,
      idToDelete: scheduleId
    }));
  };

  const handleDownloadReportClick = (report: ReportDownloadInterface) => {
    downloadFileFromUrl(report.s3Url);
  };

  const handleDatePickerClose = (newValues: RangeDates) => {
    setState(prev => ({
      ...prev,
      cacheSeed: [
        prev.cacheSeed[0],
        newValues.startDate?.toDate()?.toISOString() || null,
        newValues.endDate?.endOf('day')?.toDate()?.toISOString() || null
      ],
      filterDateValues: newValues
    }));
  };

  const handleDatesChanges = (newValues: RangeDates) => {
    if (newValues.startDate === null && newValues.endDate === null) {
      handleDatePickerClose(newValues);
    } else {
      setState(prev => ({
        ...prev,
        filterDateValues: newValues
      }));
    }
  };

  const handlePromptClose = () => {
    setState(prev => ({
      ...prev,
      idToDelete: undefined
    }));
  };

  const cacheSeedWithFilters = `reportDownloads_${cacheSeed.join('_')}_${textSearch}`;

  const config = {
    emptyState: defaultEmptyState,
    onChangeSelection: handleSelectionChange,
    pagination: {
      async: {
        formatRequestArgs: (page: number, itemsPerPage: number) => ({
          method: 'GET' as const,
          params: new URLSearchParams({
            ...(textSearch && { filterName: textSearch }),
            ...(filterDateValues.startDate && {
              filterStartDate: filterDateValues.startDate.startOf('day').toDate().toISOString()
            }),
            ...(filterDateValues.endDate && {
              filterEndDate: filterDateValues.endDate.endOf('day').toDate().toISOString()
            }),
            limit: itemsPerPage.toString(),
            order: 'desc',
            page: (page + 1).toString(),
            sortBy: 'modified'
          })
        }),
        parseData: (data: { items: ReportDownloadInterface[] }) =>
          data?.items?.map(reportDownload => {
            return {
              entity: reportDownload,
              id: reportDownload._id,
              reportingDownloadsActions: {
                actions: [
                  {
                    color: undefined,
                    icon: 'fa-regular fa-arrow-down-to-line',
                    id: `download-report-${reportDownload._id}`,
                    onClick: () => handleDownloadReportClick(reportDownload)
                  },
                  ...(useBackups
                    ? [
                        {
                          icon: 'fa-regular fa-trash',
                          id: `delete-report-${reportDownload._id}`,
                          onClick: () => handleDeleteScheduledReportClick(reportDownload._id)
                        }
                      ]
                    : [])
                ],
                className: 'reportingActions',
                ...(!useBackups && {
                  contextualMenuProps: {
                    buttons: [
                      {
                        icon: 'fa-solid fa-plus',
                        id: `new-from-report-${reportDownload._id}`,
                        onClick: () => handleNewFromReportClick(reportDownload),
                        text: formatMessage({
                          defaultMessage: 'New from Report',
                          id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.openReportButton'
                        })
                      },
                      {
                        icon: 'fa-regular fa-trash',
                        id: `delete-report-${reportDownload._id}`,
                        onClick: () => handleDeleteScheduledReportClick(reportDownload._id),
                        text: formatMessage({
                          defaultMessage: 'Delete Report',
                          id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.deleteButton'
                        })
                      }
                    ],
                    className: 'reportingSecondaryActions',
                    id: 'reportingDownloadsSecondaryActions'
                  }
                })
              },
              reportingDownloadsFileType: {
                text: formatMessage({
                  description: reportDownload.type || 'N/A',
                  id: `reporting.reportingDownload.myReportsModal.fileTypes.${reportDownload.type}`
                })
              },
              reportingDownloadsSubTitle: {
                isSubTitle: true,
                text: moment(reportDownload.modified).format(LAST_DOWNLOAD_FORMAT)
              },
              reportingDownloadsTitle: {
                text: reportDownload.name
              }
            };
          }) || [],
        refetchDataKey: cacheSeedWithFilters,
        url: `${taptap.config.backendUrlBase}report/reportDownloads/${userId}/${campaignsIds.join(',')}`
      },
      customSkeletonNumber: 4,
      show: true,
      type: 'compact'
    },
    searchEmptyState: searchDefaultEmptyState,
    selectableItem: true,
    toolbar,
    type: 'list'
  };

  return (
    <ReportsDownloadsTabStyle className="reportsDownloadsTab tabContainer" id={id}>
      <div className="tabFilters">
        <div className="reportsDownloadsTab__dateFilter">
          <DateRangePicker
            displayFormat={dateFormat}
            endDate={filterDateValues.endDate}
            endDateId={`${id}_endDate`}
            isOutsideRange={() => false}
            minimumNights={0}
            onClose={handleDatePickerClose}
            onDatesChange={handleDatesChanges}
            showClearDates
            startDate={filterDateValues.startDate}
            startDateId={`${id}_beginDate`}
          />
        </div>
        <div className="reportsDownloadsTab__searchbarFilter">
          <InputSearch
            id={`searchInput-${id}`}
            lens
            name="search"
            onChange={handleSearchInputChange}
            placeholder={formatMessage({
              defaultMessage: 'Search by name...',
              id: 'reporting.reportingDownload.myReportsModal.searchInputPlaceHolder'
            })}
          />
        </div>
      </div>
      <div className="tabActions">
        <ButtonUI
          disabled={!selectedReports.length}
          icon="fa-regular fa-arrow-down-to-line"
          id={`downloadButton-${id}`}
          loading={isDownloadLoading}
          onClick={handleDownloadMultipleReports}
          shape="link"
          text={`${formatMessage({
            defaultMessage: 'Download Reports',
            id: 'reporting.reportingDownload.myReportsModal.reportsDownloadsTab.downloadButton'
          })}${selectedReports.length ? ` (${selectedReports.length})` : ''}`}
          type="secondary"
        />
      </div>
      <TableListTS
        // @ts-expect-error
        config={config}
        gridLayout="reportingDownloadedReportsLayout"
        hideSelectAllCheckbox
        hideShowSelectedFilter
        isCollapsable
        isSelectableList
        withComposition
      />
      {idToDelete && (
        <DeleteReportPrompt
          id={`deleteReportPrompt-${id}`}
          isLoading={isDeleteFetching}
          onClose={handlePromptClose}
          onConfirm={refetchDelete}
          type="download"
        />
      )}
    </ReportsDownloadsTabStyle>
  );
}

export default ReportsDownloadsTab;
